import { Badge } from '@/components/ui/badge';
import { FINANCE_MOVEMENT_ORIGIN_TONE } from '@/lib/status-tone';
import { formatMinor } from '@/lib/money';
import type {
  FinanceAccount,
  FinanceCategory,
  FinanceMovement,
} from '@/api/generated/types.gen';

export interface MovementsTableProps {
  movements: readonly FinanceMovement[];
  accounts?: readonly FinanceAccount[];
  categories?: readonly FinanceCategory[];
  /** Called with the movement id when a row is activated. */
  onOpen?: (id: string) => void;
}

export function MovementsTable({ movements, accounts, categories, onOpen }: MovementsTableProps) {
  if (movements.length === 0) {
    // No table at all, so nothing reads as an empty grid with headers.
    return (
      <p className="rounded-[var(--radius-card)] border border-dashed border-[var(--border-hairline)] bg-card p-8 text-center text-sm text-muted">
        No payments in this window.
      </p>
    );
  }
  return (
    <div className="overflow-x-auto rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[var(--border-hairline)] text-left text-xs uppercase tracking-[0.06em] text-muted">
            <th className="px-4 py-2.5 font-semibold">Date</th>
            <th className="px-4 py-2.5 font-semibold">Account</th>
            <th className="px-4 py-2.5 font-semibold">Direction</th>
            <th className="px-4 py-2.5 font-semibold">Amount</th>
            <th className="px-4 py-2.5 font-semibold">EUR</th>
            <th className="px-4 py-2.5 font-semibold">Article</th>
            <th className="px-4 py-2.5 font-semibold">Origin</th>
          </tr>
        </thead>
        <tbody>
          {movements.map((m) => (
            <tr
              key={m.id}
              // The whole row opens the card. tabIndex + Enter/Space keep it
              // reachable without a mouse; an <a> per row would give a real
              // href but the feed hands the id to its caller instead.
              tabIndex={onOpen ? 0 : undefined}
              onClick={onOpen ? () => onOpen(m.id) : undefined}
              onKeyDown={
                onOpen
                  ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onOpen(m.id);
                      }
                    }
                  : undefined
              }
              className={[
                'border-t border-[var(--border-hairline)]',
                onOpen ? 'cursor-pointer hover:bg-[var(--surface-sunken)]' : '',
                m.reversed_by_id ? 'opacity-60' : '',
              ].join(' ')}
            >
              <td className="px-4 py-3 whitespace-nowrap">{m.occurred_on}</td>
              <td className="px-4 py-3">
                {accounts?.find((a) => a.id === m.account_id)?.name ?? m.account_id}
                <span className="ml-1 text-xs text-muted">{m.currency}</span>
              </td>
              <td className="px-4 py-3">{m.direction === 'in' ? 'In' : 'Out'}</td>
              <td className="px-4 py-3 tabular-nums">{formatMinor(m.amount_minor, m.currency)}</td>
              <td className="px-4 py-3 tabular-nums text-muted">{formatMinor(m.eur_minor, 'EUR')}</td>
              <td className="px-4 py-3">{firstArticle(m, categories)}</td>
              <td className="px-4 py-3">
                <span className="flex flex-wrap items-center gap-1">
                  <Badge variant={FINANCE_MOVEMENT_ORIGIN_TONE[m.origin].variant}>
                    {FINANCE_MOVEMENT_ORIGIN_TONE[m.origin].label}
                  </Badge>
                  {/* The two states are different facts about different rows:
                      reversed_by_id is "this was cancelled by a later mirror",
                      reverses_id is "this IS the mirror". Reading a mirror as
                      a second payment is the mistake worth preventing. */}
                  {m.reversed_by_id && <Badge variant="danger">Reversed</Badge>}
                  {m.reverses_id && <Badge variant="warning">Reversal</Badge>}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// The first line that names an article, spelled with the category's name when
// the caller supplied the list. A transfer body carries no category at all.
function firstArticle(m: FinanceMovement, categories?: readonly FinanceCategory[]): string {
  const withCat = m.lines.find((l) => l.category_id);
  if (!withCat?.category_id) return '—';
  const name = categories?.find((c) => c.id === withCat.category_id)?.name;
  const more = m.lines.filter((l) => l.category_id).length - 1;
  const label = name ?? withCat.category_id;
  return more > 0 ? `${label} +${more}` : label;
}
